"use client";

import { useState, type CSSProperties, type ReactNode } from "react";
import Link from "next/link";
import { SafeImage } from "@/components/ui/safe-image";
import { Eye } from "lucide-react";
import type { Product } from "@/types";
import type { RecentProduct } from "@/store/recently-viewed.store";
import { formatCurrency, discountPercent, mediaUrl } from "@/lib/utils";
import { QuickAddButton } from "@/components/product/quick-add-button";
import { WishlistButton } from "@/components/product/wishlist-button";
import { FlyToCart } from "@/components/product/fly-to-cart";
import dynamic from "next/dynamic";
import { CompareButton } from "@/components/product/compare-button";
import { useDealerStatus } from "@/hooks/use-dealer-status";

const QuickViewModal = dynamic(
  () => import("@/components/product/quick-view-modal").then((m) => m.QuickViewModal),
  { ssr: false }
);

const BLUR_DATA_URL = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

interface ProductCardProps {
  product: Product | RecentProduct;
  index?: number;
}

const CHIP_ROW_H = 22; // px — tek çip satırı yüksekliği

function ChipTicker({ chips }: { chips: { key: string; node: ReactNode }[] }) {
  const count = chips.length;
  if (count === 0) return null;

  if (count === 1) {
    return (
      <div className="overflow-hidden" style={{ height: CHIP_ROW_H }}>
        <div className="flex items-center" style={{ height: CHIP_ROW_H }}>{chips[0].node}</div>
      </div>
    );
  }

  const list = [...chips, { ...chips[0], key: `${chips[0].key}-clone` }];

  return (
    <div className="overflow-hidden" style={{ height: CHIP_ROW_H }}>
      <div className="animate-chip-ticker" style={{ "--chip-row-h": `${CHIP_ROW_H}px` } as CSSProperties}>
        {list.map((c) => (
          <div key={c.key} className="flex items-center" style={{ height: CHIP_ROW_H }}>
            {c.node}
          </div>
        ))}
      </div>
    </div>
  );
}

// Luxury kart: çerçeve yerine alt ince çizgi, serif başlık, uzun görsel, sol hizalı.
export function LuxuryProductCard({ product, index = 99 }: ProductCardProps) {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [isFlying, setIsFlying] = useState(false);
  const [flyStartRect, setFlyStartRect] = useState<DOMRect | undefined>(undefined);
  const discount = discountPercent(product.price, product.compareAtPrice);
  const isOutOfStock = product.trackStock && product.stock <= 0;
  const threshold = product.lowStockThreshold ?? 5;
  const isLowStock = product.trackStock && product.stock > 0 && product.stock <= threshold;
  const showNewBadge = product.isFeatured && !discount;

  const chipDefs: { key: string; node: ReactNode }[] = [];
  if (discount > 0) {
    chipDefs.push({
      key: "sale",
      node: (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm" style={{ backgroundColor: "var(--sf-badge-sale-bg)", color: "var(--sf-badge-sale-text)" }}>
          -%{discount}
        </span>
      ),
    });
  } else if (showNewBadge) {
    chipDefs.push({
      key: "new",
      node: (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-sm" style={{ backgroundColor: "var(--sf-badge-new-bg)", color: "var(--sf-badge-new-text)" }}>
          New
        </span>
      ),
    });
  }
  if (isLowStock) {
    chipDefs.push({
      key: "low",
      node: (
        <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-sm" style={{ backgroundColor: "var(--sf-badge-low-stock-bg)", color: "var(--sf-badge-low-stock-text)" }}>
          <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Son {product.stock} adet
        </span>
      ),
    });
  }
  const { isApproved, discountPercentage } = useDealerStatus();
  const dealerPrice = isApproved
    ? (product.dealerPrice ?? (discountPercentage > 0 ? product.price * (1 - discountPercentage / 100) : null))
    : null;
  const displayDiscountPercentage = product.dealerPrice && product.dealerPrice < product.price
    ? Math.round((1 - product.dealerPrice / product.price) * 100)
    : discountPercentage;

  const handleAddToCart = (btnElement: HTMLButtonElement) => {
    setFlyStartRect(btnElement.getBoundingClientRect());
    setIsFlying(true);
  };

  const handleFlyDone = () => {
    setIsFlying(false);
  };

  return (
    <>
      <FlyToCart
        isFlying={isFlying}
        startRect={flyStartRect}
        productImageUrl={mediaUrl(product.coverImageUrl)}
        onDone={handleFlyDone}
      />
      <Link href={`/products/${product.slug}`} className="group block h-full">
        <div
          className="flex h-full flex-col overflow-hidden border-b transition-colors duration-300"
          style={{ backgroundColor: "var(--sf-card-bg)", borderColor: "var(--sf-card-border)" }}
        >

        {/* Görsel — luxury daha uzun oran */}
        <div className="relative aspect-[3/4] overflow-hidden" style={{ backgroundColor: "var(--sf-bg)" }}>
          <SafeImage
            src={product.coverImageUrl ? mediaUrl(product.coverImageUrl) : undefined}
            alt={product.name}
            fill
            priority={index < 3}
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            className="object-cover card-image transition-transform duration-700 group-hover:scale-[1.04]"
            style={{ objectPosition: `${product.coverFocalX ?? 50}% ${product.coverFocalY ?? 50}%` }}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            fallbackClassName="text-gray-200"
          />

          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-900 bg-white px-3 py-1 uppercase tracking-widest">
                Tükendi
              </span>
            </div>
          )}

          <div className="absolute top-2 right-2 z-10 flex items-center gap-1 transition-all duration-200 ease-out pointer-events-auto sm:translate-y-1.5 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 sm:group-focus-within:translate-y-0 sm:group-focus-within:opacity-100">
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickViewOpen(true); }}
              aria-label="Hızlı görünüm"
              className="hidden h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-400 shadow-sm backdrop-blur-sm transition-all hover:scale-110 hover:border-gray-300 hover:text-gray-700 sm:flex"
            >
              <Eye className="h-3 w-3 transition-colors" />
            </button>
            <CompareButton product={product} size="sm" className="hidden h-7 w-7 sm:flex" />
            <WishlistButton
              productId={product.id}
              name={product.name}
              slug={product.slug}
              price={product.price}
              coverImageUrl={product.coverImageUrl}
              size="sm"
              className="h-7 w-7"
            />
          </div>

          {chipDefs.length > 0 && (
            <div className="absolute left-2 bottom-11 z-10 pointer-events-none">
              <ChipTicker chips={chipDefs} />
            </div>
          )}

          <QuickAddButton product={product} onAdd={handleAddToCart} />
        </div>

        {/* Bilgiler — sol hizalı, serif başlık */}
        <div className="flex flex-1 flex-col items-start p-3 text-left sm:p-4">
          {product.brandName && (
            <p className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-[0.18em] truncate w-full">
              {product.brandName}
            </p>
          )}
          <h3
            className="text-sm line-clamp-2 min-h-[2.5rem] mb-2 transition-colors"
            style={{ color: "var(--sf-card-title)", fontFamily: "var(--sf-font-heading)" }}
          >
            {product.name}
          </h3>
          <div className="mt-1 flex flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              {dealerPrice ? (
                <>
                  <span className="whitespace-nowrap text-xs text-gray-400 line-through">
                    {formatCurrency(product.price)}
                  </span>
                  <span className="whitespace-nowrap text-sm font-semibold" style={{ color: "var(--sf-card-price)" }}>
                    {formatCurrency(dealerPrice)}
                  </span>
                </>
              ) : (
                <span className={`whitespace-nowrap text-sm font-semibold ${isOutOfStock ? "text-gray-400" : ""}`} style={isOutOfStock ? undefined : { color: "var(--sf-card-price)" }}>
                  {formatCurrency(product.price)}
                </span>
              )}
              {product.compareAtPrice && product.compareAtPrice > product.price && !dealerPrice && (
                <span className="whitespace-nowrap text-xs text-gray-400 line-through">
                  {formatCurrency(product.compareAtPrice)}
                </span>
              )}
            </div>
            {dealerPrice && displayDiscountPercentage > 0 && (
              <span className="text-[10px] font-bold text-emerald-600">
                %{displayDiscountPercentage} İndirimli
              </span>
            )}
          </div>
        </div>

      </div>
    </Link>

    {quickViewOpen && (
      <QuickViewModal slug={product.slug} onClose={() => setQuickViewOpen(false)} />
    )}
  </>
  );
}
